import GameModel from "../../GameModel";
import TileMinedLogic from "../../logic/TileMinedLogic";
import GridTile from "../grid/GridTile";
import VectorField from "../grid/VectorField";
import WorldGrid from "../grid/WorldGrid";
import PerformanceMetrics from "../performance/PerformanceMetrics";
import DirectLinePathFinder from "./find/DirectLinePathFinder";
import PathFinder from "./find/PathFinder";
import Path from "./Path";
import PathPlan from "./plan/PathPlan";
import PathPlanEvaluator from "./plan/PathPlanEvaluator";

export default class PathPlanFinder extends GameModel {
    /**
     * @param {WorldGrid} worldGrid
     * @param {VectorField} vectorField 
     * @param {TileMinedLogic} tileMinedLogic 
     */
    constructor(worldGrid, vectorField, tileMinedLogic) {
        super();

        /**
         * @constant
         * @type {VectorField}
         */
        this.vectorField = vectorField;
        /**
         * @constant
         * @type {PathFinder}
         */
        this.pathFinder = new PathFinder();

        /**
         * @constant
         * @type {DirectLinePathFinder}
         */
        this.directLinePathFinder = new DirectLinePathFinder(worldGrid);

        /**
         * @constant
         * @type {PathPlanEvaluator}
         */
        this.pathPlanEvaluator = new PathPlanEvaluator(tileMinedLogic);

        /**
         * @constant
         * @type {Path}
         */
        this.path = new Path();
    }

    /**
     * @private
     * @param {GridTile} start 
     * @param {GridTile} destination 
     * @param {Array<PathPlan>} pathPlan The output parameter.
     * @param {boolean} mineThroughWallsEnabled 
     * @param {boolean} ignoreWallPenalty
     */
    findPathPlan(start, destination, pathPlan, mineThroughWallsEnabled, ignoreWallPenalty) {

        this.path.reset();

        PerformanceMetrics.startTime('PathFinding');
        this.pathFinder.findPath(this.path, start, destination, true, mineThroughWallsEnabled, ignoreWallPenalty); // <-- path is an output variable.
        PerformanceMetrics.endTime('PathFinding');

        if (this.path.size > 0) {
            this.pathPlanEvaluator.evaluatePath(this.path, pathPlan, false);
        }
        else {
            pathPlan.length = 0;
        }
    }

    // /**
    //  * @private
    //  * @param {Path} path 
    //  */
    // printPath(path) {
    //     let s = '';
    //     for (let i = 0; i < path.size; i++) {
    //         s += 'i=' + i + ':' + path.tiles[i].origin.toString() + '  ';
    //     }
    //     console.log('path=' + s);
    // }

    /**
     * @private
     * @param {GridTile} start 
     * @param {GridTile} destination 
     * @param {Array<PathPlan>} pathPlan The output parameter.
     * @returns {boolean} True if the direct path was found.
     */
    findDirectPathPlan(start, destination, pathPlan) {
        this.path.reset();

        PerformanceMetrics.startTime('DirectPath');
        const result = this.directLinePathFinder.findPath(this.path, start, destination); // <-- path is an output variable.
        PerformanceMetrics.endTime('DirectPath');

        if (!result) {
            return false;
        }

        if (this.path.size > 0) {
            this.pathPlanEvaluator.evaluatePath(this.path, pathPlan, true); // true = tileHealthNegligible.  always true for direct paths.

            ///////console.log('path=' + this.path.size + ' plan=' + pathPlan.length + ' ' + this.pathPlanEvaluator.pathPlanToString(pathPlan));

        }
        else {
            pathPlan.length = 0;
        }

        return true;
    }

    // /**
    //  * @private
    //  * @param {GridTile} start 
    //  * @param {GridTile} destination 
    //  * @param {Array<PathPlan>} pathPlan The output parameter.
    //  * @returns {boolean}
    //  */
    // findContinuedDirectPathPlan(start, destination, pathPlan) {

    //     // If there is no current path plan, or it is very short, then just find a new path.
    //     if (pathPlan.length < 1) {
    //         //console.log('PathPlanFinder.findContinuedDirectPathPlan() < 1 steps. using normal');
    //         return this.findDirectPathPlan(start, destination, pathPlan);
    //     }

    //     // Check to see if the existing path already goes to the destination.
    //     const pathStep = pathPlan[0];
    //     if (pathStep.endTile === destination) {
    //         pathPlan.length = 1; // chop off the plan after this step.
    //         return true;
    //     }

    //     // We want to keep the closest path step in our plan, so start at the next index.
    //     const bestStartTile = pathStep.endTile;
    //     const continuedStartIndex = 1;

    //     this.path.reset();

    //     // 2. Find path using that tile as the start position.
    //     PerformanceMetrics.startTime('DirectPath');
    //     const result = this.directLinePathFinder.findPath(this.path, bestStartTile, destination); // <-- path is an output variable.
    //     PerformanceMetrics.endTime('DirectPath');

    //     if (!result) {
    //         //console.log('PathPlanFinder.findContinuedDirectPathPlan() failed to find path from best start tile. defaulting to normal.');
    //         return this.findDirectPathPlan(start, destination, pathPlan);
    //     }

    //     // Special case: 1 step in path.
    //     if (this.path.size === 1) {
    //         ///////console.log('DIRECT PATH SIZE 1.  dest=' + destination.origin.toString() + ' path[0]=' + this.path.tiles[0].origin.toString());
    //         // Clear out the path plan.
    //         console.log('PathPlanFinder.findContinuedDirectPathPlan() resulting path length 1');
    //         pathPlan.length = 0;
    //         return;
    //     }

    //     // 3. combine paths
    //     if (this.path.size > 0) {
    //         this.pathPlanEvaluator.connectPathToPlan2(this.path, pathPlan, continuedStartIndex, true); // true = tileHealthNegligible.  always true for direct paths.
    //         return true;
    //     }
    //     else {
    //         console.log('PathPlanFinder.findContinuedDirectPathPlan() did not find a good path.');
    //         pathPlan.length = 0;
    //         return false;
    //     }
    // }

    /**
     * @private
     * @param {GridTile} start 
     * @param {GridTile} destination 
     * @param {Array<PathPlan>} pathPlan The output parameter.
     * @returns {boolean}
     */
    findContinuedDirectPathPlan2(start, destination, pathPlan) {

        // If there is no current path plan, or it is very short, then just find a new path.
        if (pathPlan.length < 1) {
            return this.findDirectPathPlan(start, destination, pathPlan);
        }

        // Check to see if the existing path already goes to the destination.
        const pathStep = pathPlan[0];
        if (pathStep.endTile === destination) {
            pathPlan.length = 1; // chop off the plan after this step.
            return true;
        }

        this.path.reset();

        // 2. Find path using that tile as the start position.
        PerformanceMetrics.startTime('DirectPath');
        const result = this.directLinePathFinder.findPath(this.path, pathStep.endTile, destination); // <-- path is an output variable.
        PerformanceMetrics.endTime('DirectPath');

        if (!result) {
            //console.log('PathPlanFinder.findContinuedDirectPathPlan() failed to find path from best start tile. defaulting to normal.');
            return this.findDirectPathPlan(start, destination, pathPlan);
        }

        // Special case: 1 step in path.
        if (this.path.size === 1) {
            ///////console.log('DIRECT PATH SIZE 1.  dest=' + destination.origin.toString() + ' path[0]=' + this.path.tiles[0].origin.toString());
            // Clear out the path plan.
            console.log('PathPlanFinder.findContinuedDirectPathPlan() resulting path length 1');
            pathPlan.length = 0;
            return;
        }

        // 3. combine paths
        if (this.path.size > 0) {
            this.pathPlanEvaluator.connectPathToPlan2(this.path, pathPlan, 1, true); // true = tileHealthNegligible.  always true for direct paths.
            return true;
        }
        else {
            console.log('PathPlanFinder.findContinuedDirectPathPlan() did not find a good path.');
            pathPlan.length = 0;
            return false;
        }
    }

    /**
     * This version always starts and the end of the first step in the path plan.
     * @private
     * @param {GridTile} start 
     * @param {GridTile} destination 
     * @param {Array<PathPlan>} pathPlan The output parameter.
     * @param {boolean} mineThroughWallsEnabled 
     * @param {boolean} ignoreWallPenalty
     */
    findContinuedPathPlan2(start, destination, pathPlan, mineThroughWallsEnabled, ignoreWallPenalty) {

        // If there is no current path plan, then just find a new path.
        if (pathPlan.length < 1) {
            //console.log('PathPlanFinder.findContinuedPathPlan() defaulting to normal finding');
            this.findPathPlan(start, destination, pathPlan, mineThroughWallsEnabled, ignoreWallPenalty);
            return;
        }

        // Clear out the path plan after the first step.
        //////pathPlan.length = 1;

        // Check to see if the existing path already goes to the destination.
        const pathStep = pathPlan[0];
        if (pathStep.endTile === destination) {
            pathPlan.length = 1; // chop off the plan after this step.
            return true;
        }

        this.path.reset();

        // 2. Find path using that tile as the start position.
        PerformanceMetrics.startTime('PathFinding');
        this.pathFinder.findPath(this.path, pathStep.endTile, destination, true, mineThroughWallsEnabled, ignoreWallPenalty); // <-- path is an output variable.
        PerformanceMetrics.endTime('PathFinding');

        if (this.path.size === 1) {
            console.log('NORMAL PATH SIZE 1.  dest=' + destination.origin.toString() + ' path[0]=' + this.path.tiles[0].origin.toString());
            // Clear out the path plan.
            pathPlan.length = 0;
            return;
        }

        // 3. combine paths

        if (this.path.size > 0) {
            this.pathPlanEvaluator.connectPathToPlan2(this.path, pathPlan, 1, ignoreWallPenalty);
        }
        else {
            console.log('PathPlanFinder.findContinuedPathPlan2() did not find a good path.');
        }
    }

    // /**
    //  * @private
    //  * @param {GridTile} start 
    //  * @param {GridTile} destination 
    //  * @param {Array<PathPlan>} pathPlan The output parameter.
    //  * @returns {boolean}
    //  */
    // findContinuedDirectPathPlan(start, destination, pathPlan) {

    //     // If there is no current path plan, or it is very short, then just find a new path.
    //     if (pathPlan.length < 1) {
    //         //console.log('PathPlanFinder.findContinuedDirectPathPlan() < 1 steps. using normal');
    //         return this.findDirectPathPlan(start, destination, pathPlan);
    //     }

    //     // 1. Find step in the current plan - this is inclusive. We want to keep this step in the new path.
    //     const closestPathStepIndex = this.findClosestPathStepIndex(pathPlan, destination);
    //     if (closestPathStepIndex === -1) {
    //         console.log('PathPlanFinder.findContinuedDirectPathPlan() FAILED to find best start index');
    //         return this.findDirectPathPlan(start, destination, pathPlan);
    //     }

    //     // Check to see if the existing path already goes to the destination.
    //     const pathStep = pathPlan[closestPathStepIndex];
    //     if (pathStep.endTile === destination) {
    //         pathPlan.length = closestPathStepIndex + 1; // chop off the plan after this step.
    //         return true;
    //     }

    //     // We want to keep the closest path step in our plan, so start at the next index.
    //     const bestStartTile = pathStep.endTile;
    //     const continuedStartIndex = closestPathStepIndex + 1;

    //     this.path.reset();

    //     // 2. Find path using that tile as the start position.
    //     PerformanceMetrics.startTime('DirectPath');
    //     const result = this.directLinePathFinder.findPath(this.path, bestStartTile, destination); // <-- path is an output variable.
    //     PerformanceMetrics.endTime('DirectPath');

    //     if (!result) {
    //         //console.log('PathPlanFinder.findContinuedDirectPathPlan() failed to find path from best start tile. defaulting to normal.');
    //         return this.findDirectPathPlan(start, destination, pathPlan);
    //     }

    //     // Special case: 1 step in path.
    //     if (this.path.size === 1) {
    //         ///////console.log('DIRECT PATH SIZE 1.  dest=' + destination.origin.toString() + ' path[0]=' + this.path.tiles[0].origin.toString());
    //         // Clear out the path plan.
    //         console.log('PathPlanFinder.findContinuedDirectPathPlan() resulting path length 1');
    //         pathPlan.length = 0;
    //         return;
    //     }

    //     // 3. combine paths
    //     if (this.path.size > 0) {
    //         //console.log('PathPlanFinder.findContinuedDirectPathPlan() CONNECTING. OLD plan length=' + pathPlan.length + ' index=' + continuedStartIndex + ' NEW path length=' + this.path.size);

    //         this.pathPlanEvaluator.connectPathToPlan2(this.path, pathPlan, continuedStartIndex, true); // true = tileHealthNegligible.  always true for direct paths.
    //         return true;
    //     }
    //     else {
    //         console.log('PathPlanFinder.findContinuedDirectPathPlan() did not find a good path.');
    //         return false;
    //     }
    // }

    // /**
    //  * @private
    //  * @param {GridTile} start 
    //  * @param {GridTile} destination 
    //  * @param {Array<PathPlan>} pathPlan The output parameter.
    //  * @param {boolean} mineThroughWallsEnabled 
    //  * @param {boolean} ignoreWallPenalty
    //  */
    // findContinuedPathPlan(start, destination, pathPlan, mineThroughWallsEnabled, ignoreWallPenalty) {

    //     // If there is no current path plan, then just find a new path.
    //     if (pathPlan.length < 1) {
    //         //console.log('PathPlanFinder.findContinuedPathPlan() defaulting to normal finding');
    //         this.findPathPlan(start, destination, pathPlan, mineThroughWallsEnabled, ignoreWallPenalty);
    //         return;
    //     }

    //     // 1. Find step in the current plan - this is inclusive. We want to keep this step in the new path.
    //     const closestPathStepIndex = this.findClosestPathStepIndex(pathPlan, destination);
    //     if (closestPathStepIndex === -1) {
    //         console.log('PathPlanFinder.findContinuedPathPlan() FAILED to find best start index.');
    //         this.findPathPlan(start, destination, pathPlan, mineThroughWallsEnabled, ignoreWallPenalty);
    //         return;
    //     }

    //     // Check to see if the existing path already goes to the destination.
    //     const pathStep = pathPlan[closestPathStepIndex];
    //     if (pathStep.endTile === destination) {
    //         pathPlan.length = closestPathStepIndex + 1; // chop off the plan after this step.
    //         return true;
    //     }

    //     // We want to keep the closest path step in our plan, so start at the next index.
    //     const bestStartTile = pathStep.endTile;
    //     const continuedStartIndex = closestPathStepIndex + 1;

    //     this.path.reset();

    //     // 2. Find path using that tile as the start position.
    //     PerformanceMetrics.startTime('PathFinding');
    //     this.pathFinder.findPath(this.path, bestStartTile, destination, true, mineThroughWallsEnabled, ignoreWallPenalty); // <-- path is an output variable.
    //     PerformanceMetrics.endTime('PathFinding');

    //     if (this.path.size === 1) {
    //         console.log('NORMAL PATH SIZE 1.  dest=' + destination.origin.toString() + ' path[0]=' + this.path.tiles[0].origin.toString());
    //         // Clear out the path plan.
    //         pathPlan.length = 0;
    //         return;
    //     }

    //     // 3. combine paths

    //     if (this.path.size > 0) {
    //         ///////this.pathPlanEvaluator.evaluatePath(this.path, pathPlan, false);
    //         ////////console.log('PathPlanFinder.findContinuedPathPlan() found path from best start tile, connecting path to plan');

    //         //console.log('PathPlanFinder.findContinuedPathPlan() CONNECTING. OLD plan length=' + pathPlan.length + ' index=' + continuedStartIndex + ' NEW path length=' + this.path.size);
    //         this.pathPlanEvaluator.connectPathToPlan2(this.path, pathPlan, continuedStartIndex, ignoreWallPenalty);
    //     }
    //     else {
    //         console.log('PathPlanFinder.findContinuedPathPlan() did not find a good path.');
    //     }
    // }


    /**
     * @private
     * @param {Array<PathPlan>} pathPlan The output parameter.
     * @param {GridTile} destination 
     * @returns {number}
     */
    findClosestPathStepIndex(pathPlan, destination) {
        let closestTile = null;
        let closestDist2 = 1000000;
        let bestIndex = -1;

        const destOrigin = destination.origin;

        for (let i = 0; i < pathPlan.length; i++) {
            const planStep = pathPlan[i];
            if (!planStep) {
                continue;
            }

            const stepEnd = planStep.endTile;

            // Small chance tha the endpoint of the step is the destination.
            if (stepEnd === destination) {
                return i;
            }

            const dist2 = destOrigin.distanceSquared(stepEnd.origin);

            if (!closestTile || closestDist2 > dist2) {
                closestTile = stepEnd;
                closestDist2 = dist2;
                bestIndex = i;
            }
        }

        return bestIndex;
    }

    /**
     * @param {GridTile} start 
     * @param {GridTile} destination 
     * @param {Array<PathPlan>} pathPlan The output parameter.
     */
    findMonsterPathPlan(start, destination, pathPlan) {
        this.findPathPlan(start, destination, pathPlan, true, false);
    }

    // /**
    //  * @param {GridTile} start 
    //  * @param {GridTile} destination 
    //  * @param {Array<PathPlan>} pathPlan The output parameter.
    //  */
    // findPartyCharacterPathPlan(start, destination, pathPlan) {

    //     if (start === destination) {
    //         console.log('findPartyCharacterPathPlan() start == destination.');
    //         pathPlan.length = 0;
    //         return;
    //     }

    //     if (this.vectorField.tileHealthNegligible && this.findContinuedDirectPathPlan(start, destination, pathPlan)) {
    //         return;
    //     }

    //     this.findPathPlan(start, destination, pathPlan, true, this.vectorField.tileHealthNegligible);
    //     ///////this.findContinuedPathPlan(start, destination, pathPlan, true, this.vectorField.tileHealthNegligible); // TODO an experiment
    // }

    /**
     * @param {GridTile} start 
     * @param {GridTile} destination 
     * @param {Array<PathPlan>} pathPlan The output parameter.
     * @param {boolean} startAtStart If false, then we need to start the path at a point along the existing path plan.
     */
    findPartyCharacterPathPlan(start, destination, pathPlan, startAtStart) {

        if (start === destination) {
            console.log('findPartyCharacterPathPlan() start == destination.');
            pathPlan.length = 0;
            return;
        }

        if (this.vectorField.tileHealthNegligible) {
            if (startAtStart || pathPlan.length === 0) {
                if (this.findDirectPathPlan(start, destination, pathPlan)) {
                    return;
                }
            }
            else {
                if (this.findContinuedDirectPathPlan2(start, destination, pathPlan)) { 
                    return;
                }
            }
        }

        // if (this.vectorField.tileHealthNegligible && this.findContinuedDirectPathPlan2(start, destination, pathPlan, startAtStart)) {
        //     return;
        // }

        if (startAtStart || pathPlan.length === 0) {
            this.findPathPlan(start, destination, pathPlan, true, this.vectorField.tileHealthNegligible);    
        }
        else {
            this.findContinuedPathPlan2(start, destination, pathPlan, true, this.vectorField.tileHealthNegligible);
        }

        ///////this.findContinuedPathPlan(start, destination, pathPlan, true, this.vectorField.tileHealthNegligible); // TODO an experiment
    }

    ////////////////////////////////////
    // GameModel
    ////////////////////////////////////

    /**
     * @override
     */
    resetFull() {
        this.path.reset();
    }

    /**
     * @override
     */
    resetForPrestige() {
        this.resetFull();
    }
}